import { useState, type RefObject } from 'react'
import { formatCents, parseMoneyInput } from '../../api/parseMoney'
import { useToast } from '../../platform/feedback/ToastProvider'
import { Overlay } from '../../platform/overlay/Overlay'
import { Button } from '../../platform/primitives/Button'
import { Field } from '../../platform/primitives/Field'
import type { Category } from '../categories/categoryTree'
import { CategoryField } from '../categories/picker/CategoryField'
import { overlapWarning } from './budgetRules'
import { DEFAULT_ALERT_PCT, type Envelope, type EnvelopeDraft } from './budgetWire'
import {
  BudgetWriteError,
  WRITE_COPY,
  useCreateBudget,
  useUpdateBudget,
} from './useBudgetWrites'
import './budget.css'

export const FORM_COPY = {
  nameRequired: 'Give this envelope a name.',
  amountRequired: 'Enter an amount greater than zero.',
  amountUnreadable: "That isn't an amount we can read.",
  thresholdRange: 'Choose a percentage from 1 to 100.',
  noCategory: 'No category: this envelope counts all your spending.',
} as const

type Mode = { kind: 'create' } | { kind: 'edit'; envelope: Envelope }

type Props = {
  mode: Mode
  /** For the overlap warning. */
  active: readonly Envelope[]
  categories: readonly Category[]
  onClose: () => void
  restoreFocusTo?: RefObject<HTMLElement | null>
}

type Errors = Partial<Record<'name' | 'amount' | 'threshold', string>>

/**
 * The envelope sheet: create and edit in an `Overlay` — full-screen on a
 * phone (M4-R22). Pause/Resume and Delete live on the detail page (`BudgetDetail`). It owns its writes
 * (the `CategoryForm` precedent). Web creates monthly only (A3), so a period is
 * stated, never chosen, and is never sent on an edit.
 */
export function BudgetForm({ mode, active, categories, onClose, restoreFocusTo }: Props) {
  const editing = mode.kind === 'edit' ? mode.envelope : null
  const [name, setName] = useState(editing?.name ?? '')
  const [categoryId, setCategoryId] = useState<string | null>(editing?.categoryId ?? null)
  const [amountText, setAmountText] = useState(editing ? formatCents(editing.amountCents) : '')
  const [thresholdText, setThresholdText] = useState(String(editing?.alertThresholdPct ?? DEFAULT_ALERT_PCT))
  const [errors, setErrors] = useState<Errors>({})
  const [categoryError, setCategoryError] = useState<string | null>(null)
  const create = useCreateBudget()
  const update = useUpdateBudget()
  const toast = useToast()
  const pending = create.isPending || update.isPending

  const warning = overlapWarning(categoryId, editing?.id ?? null, active, categories)

  function pickCategory(id: string | null) {
    setCategoryId(id)
    setCategoryError(null)
    // A typed name is never overwritten.
    if (id !== null && name.trim() === '') {
      const picked = categories.find((c) => c.id === id)
      if (picked) setName(picked.name)
    }
  }

  function check(): { draft: EnvelopeDraft } | { errors: Errors } {
    const found: Errors = {}
    if (name.trim() === '') found.name = FORM_COPY.nameRequired
    let amountCents = 0
    if (amountText.trim() === '') found.amount = FORM_COPY.amountRequired
    else {
      try {
        amountCents = parseMoneyInput(amountText)
        if (amountCents <= 0) found.amount = FORM_COPY.amountRequired
      } catch {
        found.amount = FORM_COPY.amountUnreadable
      }
    }
    const t = thresholdText.trim()
    const alertThresholdPct = /^\d{1,3}$/.test(t) ? Number(t) : 0
    if (alertThresholdPct < 1 || alertThresholdPct > 100) found.threshold = FORM_COPY.thresholdRange
    if (Object.keys(found).length > 0) return { errors: found }
    return { draft: { name, categoryId, amountCents, alertThresholdPct } }
  }

  function fail(cause: unknown) {
    const kind = cause instanceof BudgetWriteError ? cause.kind : 'failed'
    if (kind === 'category') setCategoryError(WRITE_COPY.category)
    else toast.show({ kind: 'error', message: WRITE_COPY[kind] })
  }

  async function submit() {
    const result = check()
    if ('errors' in result) {
      setErrors(result.errors)
      return
    }
    setErrors({})
    try {
      if (editing) await update.mutateAsync({ before: editing, draft: result.draft })
      else await create.mutateAsync(result.draft)
      onClose()
    } catch (cause) {
      fail(cause)
    }
  }

  const title = editing ? 'Edit envelope' : 'New envelope'
  return (
    <>
      <Overlay open onClose={onClose} label={title} placement="sheet" surface="glass" restoreFocusTo={restoreFocusTo}>
        <form
          className="budget-form"
          noValidate
          onSubmit={(e) => {
            e.preventDefault()
            void submit()
          }}
        >
          <h3 className="budget-form__title">{title}</h3>
          <Field
            label="Name"
            size="form"
            value={name}
            error={errors.name}
            onChange={(e) => {
              setName(e.target.value)
              setErrors((p) => ({ ...p, name: undefined }))
            }}
          />
          <div className="budget-form__category">
            <CategoryField label="Category" value={categoryId} onChange={pickCategory} allowNone />
            {categoryId === null && <p className="budget-form__hint">{FORM_COPY.noCategory}</p>}
            {categoryError !== null && (
              <p className="budget-form__error" role="alert">
                {categoryError}
              </p>
            )}
            {warning !== null && (
              <p className="budget-form__note" role="status">
                {warning}
              </p>
            )}
          </div>
          <Field
            label="Monthly amount"
            size="form"
            inputMode="decimal"
            value={amountText}
            error={errors.amount}
            onChange={(e) => {
              setAmountText(e.target.value)
              setErrors((p) => ({ ...p, amount: undefined }))
            }}
          />
          <Field
            label="Alert me at"
            size="form"
            type="number"
            min={1}
            max={100}
            hint="% of the envelope"
            value={thresholdText}
            error={errors.threshold}
            onChange={(e) => {
              setThresholdText(e.target.value)
              setErrors((p) => ({ ...p, threshold: undefined }))
            }}
          />
          <p className="budget-form__hint">{editing ? `Repeats ${editing.period}` : 'Repeats monthly'}</p>

          <div className="budget-form__actions">
            <Button type="submit" variant="primary" size="form" disabled={pending}>
              {pending ? 'Saving…' : editing ? 'Save' : 'Create envelope'}
            </Button>
            <Button type="button" variant="outline" size="form" onClick={onClose}>
              Cancel
            </Button>
          </div>

        </form>
      </Overlay>

    </>
  )
}
